import { inject, Injectable } from '@angular/core';
import {
  map, Observable, throwError
} from 'rxjs';
import { HttpService } from '../../http/service/http.service';
import {
  ValidateEntraTokenRequest,
  ValidateEntraTokenResponse,
  ValidateEntraTokenRawResponse,
  IUserInfo
} from '../type/auth.type';
import { environment } from '../../../../environments/environment';
import { encodeToBase64 } from '../../../shared/utils/helper';

@Injectable({ providedIn: 'root' })
export class AuthApiService {
  private readonly httpService = inject(HttpService);

  validateEntraToken(msalToken: string): Observable<ValidateEntraTokenResponse> {
    const msalBearerToken = msalToken.trim();

    if (msalBearerToken.length === 0) {
      return throwError(() => new Error('Microsoft access token is required'));
    }

    const request: ValidateEntraTokenRequest = { bearerToken: msalBearerToken };

    return this.httpService
      .post<ValidateEntraTokenRawResponse>(`${environment.apiBaseUrl}/api/authentication/entra/token-user`, request)
      .pipe(map((response) => this.mapTokenResponse(response)));
  }

  validateEmail(email: string): Observable<IUserInfo> {
    return this.httpService
      .post<IUserInfo>(`${environment.apiBaseUrl}/api/authentication/email/validation`, { email });
  }

  usernamePasswordLogin(
    email: string,
    password: string,
    deviceId: string
  ): Observable<ValidateEntraTokenResponse> {
    const encodedPassword = encodeToBase64(password);

    return this.httpService
      .post<ValidateEntraTokenRawResponse>(`${environment.apiBaseUrl}/api/authentication/usernamepassword`,
        {
          username: email,
          password: encodedPassword,
          deviceId,
          isRememberMe: true,
        })
      .pipe(map((response) => this.mapTokenResponse(response)));
  }

  refreshToken(token: string): Observable<ValidateEntraTokenResponse> {
    const normalizedToken = token.trim();

    if (normalizedToken.length === 0) {
      return throwError(() => new Error('Backend access token is required for refresh'));
    }

    return this.httpService
      .post<ValidateEntraTokenRawResponse>(
        `${environment.apiBaseUrl}/api/authentication/refreshtoken`,
        null,
        { headers: { Authorization: `Bearer ${normalizedToken}` } }
      )
      .pipe(map((response) => this.mapTokenResponse(response)));
  }

  usernamePasswordSendEmailMFA(email: string): Observable<void> {
    return this.httpService
      .post<void>(`${environment.apiBaseUrl}/api/authentication/multifactorauthentication/email/send`, { email });
  }

  private mapTokenResponse(response: ValidateEntraTokenRawResponse): ValidateEntraTokenResponse {
    const bearerToken = typeof response.bearerToken === 'string'
      ? undefined
      : response.bearerToken;

    const token = bearerToken?.accessToken
      ?? response.token
      ?? response.accessToken
      ?? (typeof response.bearerToken === 'string' ? response.bearerToken : undefined);

    if (token === undefined || token === null || token.trim().length === 0) {
      throw new Error('Backend token response did not include a token');
    }

    return {
      token,
      expiresAt: bearerToken?.expiresAtUtc ?? response.expiresAtUtc ?? response.expiresAt,
      expiresIn:
        bearerToken?.expiresInSeconds
        ?? response.expiresInSeconds
        ?? response.expiresIn
        ?? response.expires_in,
      userProfile: response.userProfile,
    };
  }
}
